import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { auth } from '../lib/firebase.js';

export default function AuthDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const firebaseConfigured = !!auth;
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  const validEmail = /.+@.+\..+/.test(email.trim());
  const validPassword = password.trim().length >= 6;

  return (
    <div className="fixed inset-0 bg-black/70 z-[3000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-sm w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <div className="font-semibold">{mode === 'signIn' ? 'Sign in' : 'Create account'}</div>
          <button className="text-sm px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={onClose}>Close</button>
        </div>
        <div className="p-4 space-y-3">
          {!firebaseConfigured && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
              <strong>Firebase not configured.</strong> Create a <code>.env.local</code> file with your Firebase credentials and restart the dev server.
            </div>
          )}
          <button
            onClick={async () => { if (!firebaseConfigured) { setErr('Firebase not configured'); return; } setBusy(true); setErr(null); try { await signInWithGoogle(); onClose(); } catch (e: any) { setErr('Google sign-in failed'); } finally { setBusy(false);} }}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded px-4 py-2"
            disabled={busy || !firebaseConfigured}
          >
            Continue with Google
          </button>
          <div className="text-xs text-gray-500 text-center">or use email</div>
          <input value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="Email" className="w-full border rounded px-3 py-2" />
          <input value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Password" type="password" className="w-full border rounded px-3 py-2" />
          {err && <div className="text-red-600 text-sm">{err}</div>}
          {mode === 'signIn' ? (
            <button
              onClick={async () => { if (!validEmail || !validPassword) { setErr('Enter a valid email and password (6+ chars)'); return;} setBusy(true); setErr(null); try { await signInWithEmail(email.trim(), password.trim()); onClose(); } catch (e: any) { setErr('Email sign-in failed'); } finally { setBusy(false);} }}
              className="w-full bg-black/80 hover:bg-black/70 disabled:opacity-50 text-white rounded px-4 py-2"
              disabled={busy}
            >
              Sign in
            </button>
          ) : (
            <button
              onClick={async () => { if (!validEmail || !validPassword) { setErr('Enter a valid email and password (6+ chars)'); return;} setBusy(true); setErr(null); try { await signUpWithEmail(email.trim(), password.trim()); onClose(); } catch (e: any) { setErr('Sign up failed'); } finally { setBusy(false);} }}
              className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white rounded px-4 py-2"
              disabled={busy}
            >
              Create account
            </button>
          )}
          <div className="text-xs text-center text-gray-500">
            {mode === 'signIn' ? (
              <button className="underline" onClick={()=> setMode('signUp')}>Need an account? Sign up</button>
            ) : (
              <button className="underline" onClick={()=> setMode('signIn')}>Have an account? Sign in</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


